import { WarningAmber } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import React from "react";
import { getScoreHex } from "../shared/scoreColors";

const getConfidenceLabel = (conf: number): string => {
  if (conf >= 0.8) return "High";
  if (conf >= 0.6) return "Medium";
  return "Low";
};

const TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      color: "text.primary",
      border: "1px solid",
      borderColor: "divider",
      boxShadow: 3,
      "& .MuiTooltip-arrow": {
        color: "background.paper",
        "&::before": {
          border: "1px solid",
          borderColor: "divider",
        },
      },
    },
  },
};

interface EvaluationScoreTooltipProps {
  evaluationData: any;
  children: React.ReactElement;
}

const EvaluationScoreTooltip: React.FC<EvaluationScoreTooltipProps> = ({
  evaluationData,
  children,
}) => {
  const overallScore = evaluationData?.overall_score;
  if (overallScore === undefined) return children;

  const criteriaResults = evaluationData?.criteria_results || {};
  const criteriaCount = Object.keys(criteriaResults).length;
  const avgConfidence =
    criteriaCount > 0
      ? Object.values(criteriaResults).reduce(
          (sum: number, r: any) => sum + (r.confidence || 0),
          0,
        ) / criteriaCount
      : 0;
  const execSummary = evaluationData?.executive_summary;
  const scoreColor = getScoreHex(overallScore);
  const normalizedScore = overallScore / 5;
  const circumference = 2 * Math.PI * 20;

  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 400, p: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <svg
                width="48"
                height="48"
                style={{ transform: "rotate(-90deg)" }}
              >
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.1)"
                  strokeWidth="4"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  fill="none"
                  stroke={scoreColor}
                  strokeWidth="4"
                  strokeDasharray={`${circumference}`}
                  strokeDashoffset={`${circumference * (1 - normalizedScore)}`}
                  strokeLinecap="round"
                />
              </svg>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    color: scoreColor,
                  }}
                >
                  {overallScore.toFixed(1)}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.25 }}
              >
                Evaluation Score
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  mb: 0.25,
                }}
              >
                {criteriaCount} criteria ·{" "}
                {getConfidenceLabel(avgConfidence)} confidence
              </Typography>
            </Box>
          </Box>
          {execSummary?.key_strengths?.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, display: "block" }}
              >
                Strengths:
              </Typography>
              {execSummary.key_strengths
                .slice(0, 3)
                .map((s: string, i: number) => (
                  <Typography
                    key={i}
                    variant="caption"
                    sx={{ display: "block", ml: 1 }}
                  >
                    • {s}
                  </Typography>
                ))}
            </Box>
          )}
          {execSummary?.key_concerns?.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, display: "block" }}
              >
                Concerns:
              </Typography>
              {execSummary.key_concerns
                .slice(0, 3)
                .map((c: string, i: number) => (
                  <Typography
                    key={i}
                    variant="caption"
                    sx={{ display: "block", ml: 1 }}
                  >
                    • {c}
                  </Typography>
                ))}
            </Box>
          )}
          {evaluationData?.eval_spec_version_number != null && (
            <>
              <Box
                sx={{
                  borderTop: "1px solid",
                  borderColor: "divider",
                  mt: 1,
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block" }}
              >
                Based on Requirements v
                {evaluationData.eval_spec_version_number}
              </Typography>
            </>
          )}
          {evaluationData?.spec_version_changed && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.25,
                color: "warning.main",
              }}
            >
              <WarningAmber sx={{ fontSize: 14 }} />
              <Typography variant="caption">Requirements changed</Typography>
            </Box>
          )}
        </Box>
      }
      arrow
      placement="top"
      componentsProps={TOOLTIP_PROPS}
    >
      {children}
    </Tooltip>
  );
};

export default EvaluationScoreTooltip;
